'use client';

import { useState } from 'react';
// import { TabGroup, TabList, Tab, TabPanels, TabPanel } from '@tremor/react'; // Tremor components removed
// import { AiGenerateIcon } from '../../assets/DocumentManagementIcon/AIGenerate'; // Not used
import { WorkSpaceDetails } from './components/WorkSpaceDetails';
import { FeatureManagement } from './components/FeatureManagement';
import { PrivacyForm } from './components/PrivacyForm';
import { IntegrationsForm } from './components/IntegrationsForm';
import { CustomFieldsForm } from './components/CustomFieldsForm';
import { PerformanceEngagementSettings } from './components/PerformanceEngagementSettings';
import { EmployeeSelfServiceSettings } from './components/EmployeeSelfServiceSettings';
import { SecurityCompliancePanel } from './components/SecurityCompliancePanel';
import { AuditLog } from './components/AuditLog';
import { LeaveTypesSettings } from './components/LeaveTypesSettings';
import { HolidaysSettings } from './components/HolidaysSettings';
import { CarryoverSettings } from './components/CarryoverSettings';
import { ApprovalWorkflowSettings } from './components/ApprovalWorkflowSettings';

// Helper function to conditionally apply class names
function classNames(...classes: (string | boolean | undefined)[]): string {
  return classes.filter(Boolean).join(' ');
}


export function SettingsRoute() {
  const [activeView, setActiveView] = useState('workspace-details'); // Default view

  const settingsNavigationItems = [
    { name: 'Workspace Details', slug: 'workspace-details', description: "Manage your workspace information and settings." },
    { name: 'Privacy', slug: 'privacy', description: "Configure your privacy settings and preferences." },
    { name: 'Features', slug: 'features', description: "Enable or disable features for your workspace." },
    { name: 'Integrations', slug: 'integrations', description: "Connect Slack, calendars, and Google Workspace." },
    { name: 'Leave Types', slug: 'leave-types', description: "The types of leave people can book, and how each one is approved and counted." },
    { name: 'Holidays', slug: 'holidays', description: "Public holidays per office and working days. Neither is deducted from leave." },
    { name: 'Approvals', slug: 'approvals', description: "The steps a leave request goes through before it is approved." },
    { name: 'Carryover', slug: 'carryover', description: "Carry unused paid holiday into the next year, up to a limit." },
    { name: 'Custom Fields', slug: 'custom-fields', description: "Define additional fields for people and company records." },
    { name: 'Performance & Engagement', slug: 'performance-engagement', description: "Customize open-ended prompts for performance workflows." },
    { name: 'Employee Self-Service', slug: 'employee-self-service', description: "Configure HR helpdesk, PTO, and benefits settings." },
    { name: 'Security & Compliance', slug: 'security-compliance', description: "Review access controls, governance score, and compliance actions." },
    { name: 'Audit Log', slug: 'audit-log', description: "Who changed what in your workspace: people, leave decisions, settings, documents and data requests." },
  ];

  const renderContent = () => {
    switch (activeView) {
      case 'workspace-details':
        return <WorkSpaceDetails />;
      case 'privacy':
        return <PrivacyForm />;
      case 'features':
        return <FeatureManagement />;
      case 'integrations':
        return <IntegrationsForm />;
      case 'leave-types':
        return <LeaveTypesSettings />;
      case 'holidays':
        return <HolidaysSettings />;
      case 'approvals':
        return <ApprovalWorkflowSettings />;
      case 'carryover':
        return <CarryoverSettings />;
      case 'custom-fields':
        return <CustomFieldsForm />;
      case 'performance-engagement':
        return <PerformanceEngagementSettings />;
      case 'employee-self-service':
        return <EmployeeSelfServiceSettings />;
      case 'security-compliance':
        return <SecurityCompliancePanel />;
      case 'audit-log':
        return <AuditLog />;
      default:
        return null;
    }
  };

  const currentNavItem = settingsNavigationItems.find(item => item.slug === activeView);

  return (
    <div className="bg-white-100 min-h-screen">
      <main className="mx-auto max-w-7xl py-10 lg:px-8">
        {/* Page Title */}
        <div className="px-4 sm:px-6 lg:px-0 mb-8">
          <h1 className="text-3xl font-bold tracking-tight text-white-900">Settings</h1>
        </div>

        {/* Secondary navigation header */}
        <header className="border-b border-white-200">
          <div className="px-4 sm:px-6 lg:px-0">
            <nav className="flex overflow-x-auto py-4">
              <ul className="flex min-w-full flex-none gap-x-6 text-sm font-semibold leading-6 text-white-500 sm:gap-x-8">
                {settingsNavigationItems.map((item) => (
                  <li key={item.name}>
                    <button
                      type="button"
                      onClick={() => {
                        setActiveView(item.slug);
                      }}
                      className={classNames(
                        activeView === item.slug ? 'text-indigo-600' : 'text-white-500 hover:text-indigo-600',
                        'whitespace-nowrap'
                      )}
                    >
                      {item.name}
                    </button>
                  </li>
                ))}
              </ul>
            </nav>
          </div>
        </header>

        {/* Content sections based on activeView */}
        <div className="divide-y divide-white-200">
          {currentNavItem && (
            <div className="grid grid-cols-1 gap-x-8 gap-y-10 px-4 py-10 sm:px-6 md:grid-cols-3 lg:px-8">
              <div>
                <h2 className="text-base font-semibold leading-7 text-white-900">{currentNavItem.name}</h2>
                <p className="mt-1 text-sm leading-6 text-white-600">
                  {currentNavItem.description}
                </p>
              </div>
              <div className="md:col-span-2">
                {renderContent()}
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
